import React, { useEffect, useState } from "react";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import { Plus, Trash2, Beef, Users, Truck, PackageOpen, Flame, Loader2, Sparkles, Percent, Landmark, Wrench, RefreshCw, TrendingUp, TrendingDown, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { TRY, trDate, formatApiError, weekendRow, openPdf } from "@/lib/api";
import { Card } from "@/components/Cards";
import SearchBox, { matches } from "@/components/SearchBox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const CATS = [
  { code: "GIDA", name: "Gıda & Hammadde", icon: Beef, color: "#E27D38" },
  { code: "PERSONEL", name: "Personel & Mutfak", icon: Users, color: "#38BDF8" },
  { code: "LOJISTIK", name: "Ulaşım & Sevkiyat", icon: Truck, color: "#F59E0B" },
  { code: "AMBALAJ", name: "Ambalaj & Sarf", icon: PackageOpen, color: "#A855F7" },
  { code: "GENEL", name: "Genel Gider", icon: Flame, color: "#64748B" },
  { code: "BANKA", name: "Banka Giderleri", icon: Landmark, color: "#0EA5E9" },
  { code: "KOMISYON", name: "Komisyon", icon: Percent, color: "#14B8A6" },
  { code: "BAKIM", name: "Bakım-Onarım", icon: Wrench, color: "#84CC16" },
  { code: "VERGI", name: "Vergi & Resmi", icon: Landmark, color: "#EF4444" },
  { code: "EKSTRA", name: "Ekstra Maliyet Kalemleri", icon: Sparkles, color: "#EC4899" },
];
const catOf = (code) => CATS.find((c) => c.code === code) || CATS[4];
const todayISO = () => new Date().toISOString().slice(0, 10);

const Diff = ({ diff, prev }) => {
  if (!diff) return <span className="font-mono text-muted-foreground">—</span>;
  const up = diff > 0;
  const pct = prev ? ` (${up ? "+" : ""}${Math.round((diff / prev) * 100)}%)` : "";
  return (
    <span className={`inline-flex items-center gap-1 font-mono ${up ? "text-rose-500" : "text-emerald-500"}`}>
      {up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
      {up ? "+" : "-"}{TRY(Math.abs(diff))}{pct}
    </span>
  );
};

const CompareRow = ({ cat, cur, prev, diff }) => (
  <tr className="border-b border-border/60" data-testid={`cost-compare-${cat.code}`}>
    <td className="py-2 pr-4"><span className="inline-flex items-center gap-1.5" style={{ color: cat.color }}><cat.icon className="h-3.5 w-3.5" /> <span className="text-foreground">{cat.name}</span></span></td>
    <td className="py-2 pr-4 text-right font-mono text-muted-foreground">{TRY(prev)}</td>
    <td className="py-2 pr-4 text-right font-mono">{TRY(cur)}</td>
    <td className="py-2 text-right"><Diff diff={diff} prev={prev} /></td>
  </tr>
);

export default function Costs() {
  const [costs, setCosts] = useState([]);
  const [summary, setSummary] = useState([]);
  const [prevSummary, setPrevSummary] = useState([]);
  const [dash, setDash] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ category: "GIDA", date: todayISO(), description: "", amount: "", supplier: "" });

  const load = async () => {
    const params = { month, ...(filter !== "ALL" ? { category: filter } : {}) };
    const [c, s, p, d] = await Promise.all([
      api.get("/costs", { params }),
      api.get("/costs/summary", { params: { month } }),
      api.get("/costs/summary", { params: { month: prevMonth } }),
      api.get("/dashboard"),
    ]);
    setCosts(c.data); setSummary(s.data); setPrevSummary(p.data); setDash(d.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, month]);
  const prevMonth = (() => { const d = new Date(`${month}-01T00:00:00`); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); })();
  const fmtMonth = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("tr-TR", { month: "long", year: "numeric" });
  const monthLabel = fmtMonth(month);
  const prevLabel = fmtMonth(prevMonth);
  const sumOf = (arr) => arr.reduce((a, s) => a + s.total, 0);
  const compareRows = CATS.map((cat) => {
    const cur = summary.find((s) => s.category === cat.code)?.total || 0;
    const prev = prevSummary.find((s) => s.category === cat.code)?.total || 0;
    return { cat, cur, prev, diff: cur - prev };
  }).filter((r) => r.cur || r.prev);
  const curTotal = sumOf(summary), prevTotal = sumOf(prevSummary);
  const shownCosts = costs.filter((c) => matches(search, c.description, catOf(c.category).name, c.supplier, c.amount, c.source === "cashbook" ? "kasa" : ""));
  const monthTotal = costs.reduce((a, c) => a + (c.amount || 0), 0);

  const save = async () => {
    if (!(parseFloat(form.amount) > 0)) { toast.error("Geçerli bir tutar girin"); return; }
    setSaving(true);
    try {
      await api.post("/costs", { ...form, amount: parseFloat(form.amount) });
      toast.success("Maliyet kaydedildi");
      setOpen(false);
      setForm({ category: "GIDA", date: todayISO(), description: "", amount: "", supplier: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openCostPdf = async () => {
    try { const r = await api.get("/costs/pdf", { params: { month }, responseType: "blob" }); openPdf(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const syncCashbook = async () => {
    try { const r = await api.post("/cashbook/sync-costs"); toast.success(r.data.message); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/costs/${id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pieData = summary.map((s) => ({ name: catOf(s.category).name, value: s.total, color: catOf(s.category).color }));
  const totalCost = summary.reduce((a, b) => a + b.total, 0);
  const compareData = dash ? [
    { name: "Bu Ay", Ciro: dash.month_revenue, Maliyet: dash.month_costs, "Net Kâr": dash.net_profit },
  ] : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Maliyet Yönetimi & Kâr/Zarar</h1>
          <p className="text-sm text-muted-foreground mt-1">Kategori bazlı gider takibi ve kârlılık analizi.</p>
        </div>
        <div className="flex gap-2">
        <Button data-testid="cost-pdf-button" variant="outline" onClick={openCostPdf} className="rounded-full h-11 px-5 gap-2">
          <FileText className="h-4 w-4" /> Aylık PDF
        </Button>
        <Button data-testid="cost-sync-cashbook-button" variant="outline" onClick={syncCashbook} className="rounded-full h-11 px-5 gap-2">
          <RefreshCw className="h-4 w-4" /> Kasa Giderlerini Aktar
        </Button>
        <Button data-testid="cost-add-button" onClick={() => setOpen(true)} className="rounded-full h-11 px-6 font-semibold gap-2">
          <Plus className="h-4 w-4" /> Maliyet Ekle
        </Button>
        </div>
      </div>

      {dash?.month_vat > 0 && (
        <Card className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-amber-400/30" data-testid="costs-vat-card">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-400/10 text-amber-400 flex items-center justify-center"><Percent className="h-5 w-5" /></div>
            <div><div className="font-heading font-semibold">Aylık Toplam KDV</div><div className="text-xs text-muted-foreground">Müşteri fiyatlarına eklenen KDV — maliyetlerden ayrı izlenir, net kârdan düşülür</div></div>
          </div>
          <div className="font-mono text-2xl font-bold text-amber-400" data-testid="costs-vat-total">{TRY(dash.month_vat)}</div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <Card className="lg:col-span-5 p-5">
          <h3 className="font-heading text-lg font-semibold mb-1">Kategoriye Göre Dağılım</h3>
          <p className="text-xs text-muted-foreground mb-2"><span className="capitalize">{monthLabel}</span> toplam: <span className="font-mono font-semibold text-foreground">{TRY(totalCost)}</span></p>
          {pieData.length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2}>
                  {pieData.map((e, i) => <Cell key={i} fill={e.color} stroke="hsl(var(--card))" strokeWidth={2} />)}
                </Pie>
                <Tooltip formatter={(v) => TRY(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="lg:col-span-7 p-5">
          <h3 className="font-heading text-lg font-semibold mb-1">Ciro vs Maliyet vs Net Kâr</h3>
          <p className="text-xs text-muted-foreground mb-4">Bu ayki karşılaştırma</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={compareData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}b` : v)} />
              <Tooltip formatter={(v) => TRY(v)} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} cursor={{ fill: "hsl(var(--accent))" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Ciro" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={70} />
              <Bar dataKey="Maliyet" fill="#F43F5E" radius={[4, 4, 0, 0]} maxBarSize={70} />
              <Bar dataKey="Net Kâr" fill="#E27D38" radius={[4, 4, 0, 0]} maxBarSize={70} />
            </BarChart>
          </ResponsiveContainer>
          {dash && (
            <div className="mt-3 text-xs text-muted-foreground text-center">
              Porsiyon başına ortalama maliyet: <span className="font-mono font-semibold text-foreground">{TRY(dash.avg_cost_per_portion)}</span>
            </div>
          )}
        </Card>
      </div>

      <div className="flex flex-wrap items-center gap-3" data-testid="cost-month-bar">
        <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="cost-month-input"
          className="h-10 rounded-md border border-input bg-background px-3 text-sm" />
        <span className="text-sm text-muted-foreground capitalize">{monthLabel}</span>
        <SearchBox value={search} onChange={setSearch} placeholder="Açıklama, sınıf, tedarikçi ara…" testId="cost-search-input" className="w-full sm:w-[300px]" />
        <span className="ml-auto text-sm text-muted-foreground">Toplam: <span className="font-mono font-semibold text-foreground" data-testid="cost-month-total">{TRY(monthTotal)}</span></span>
      </div>

      <Card className="p-5" data-testid="cost-month-compare">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="font-heading text-lg font-semibold">Ay Karşılaştırma</h3>
          <span className="text-xs text-muted-foreground capitalize">{prevLabel} → {monthLabel}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-2 pr-4">Sınıf</th>
                <th className="py-2 pr-4 text-right capitalize">{prevLabel}</th>
                <th className="py-2 pr-4 text-right capitalize">{monthLabel}</th>
                <th className="py-2 text-right">Fark</th>
              </tr>
            </thead>
            <tbody>
              {compareRows.length === 0 ? (
                <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">Her iki ayda da maliyet kaydı yok</td></tr>
              ) : compareRows.map((r) => <CompareRow key={r.cat.code} {...r} />)}
              <tr className="border-t border-border font-semibold" data-testid="cost-compare-total">
                <td className="py-2 pr-4">Toplam</td>
                <td className="py-2 pr-4 text-right font-mono">{TRY(prevTotal)}</td>
                <td className="py-2 pr-4 text-right font-mono">{TRY(curTotal)}</td>
                <td className="py-2 text-right"><Diff diff={curTotal - prevTotal} prev={prevTotal} /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Button variant={filter === "ALL" ? "default" : "outline"} onClick={() => setFilter("ALL")}
          className="h-9 rounded-full" data-testid="cost-category-filter">Tümü</Button>
        {CATS.map((c) => (
          <Button key={c.code} variant={filter === c.code ? "default" : "outline"} onClick={() => setFilter(c.code)}
            className="h-9 rounded-full gap-1.5" data-testid={`cost-filter-${c.code}`}>
            <c.icon className="h-3.5 w-3.5" /> {c.name}
          </Button>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left">
                {["Tarih", "Kategori", "Açıklama", "Tedarikçi", "Tutar", ""].map((h, i) => (
                  <th key={i} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground ${i === 4 ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownCosts.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">Maliyet kaydı bulunamadı</td></tr>
              ) : shownCosts.map((c) => {
                const cat = catOf(c.category);
                return (
                  <tr key={c.id} data-testid={`cost-row-${c.id}`} className={"border-b border-border/60 hover:bg-accent/40 transition-colors" + weekendRow(c.date)}>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{trDate(c.date)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full"
                        style={{ background: `${cat.color}20`, color: cat.color }}>
                        <cat.icon className="h-3.5 w-3.5" /> {cat.name}
                      </span>
                    </td>
                    <td className="px-4 py-3">{c.description || "—"}{c.source === "cashbook" && <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded px-1.5 py-0.5">Kasa</span>}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{c.supplier || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{TRY(c.amount)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => del(c.id)} data-testid={`cost-delete-${c.id}`}
                        className="h-7 w-7 rounded-md hover:bg-destructive/10 inline-flex items-center justify-center text-muted-foreground hover:text-destructive">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Yeni Maliyet / Fatura</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <F label="Kategori">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="cost-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <div className="grid grid-cols-2 gap-4">
              <F label="Tutar (₺)">
                <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="cost-form-amount" className="font-mono" placeholder="0" />
              </F>
              <F label="Tarih">
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="cost-form-date" className="font-mono" />
              </F>
            </div>
            <F label="Açıklama">
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="cost-form-description" placeholder="Örn: Et & tavuk alımı" />
            </F>
            <F label="Tedarikçi / Personel">
              <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                data-testid="cost-form-supplier" placeholder="Opsiyonel" />
            </F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} disabled={saving} data-testid="cost-form-save" className="gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);
